/* ============================================================================
   AGENDA · contenedor

   La LÓGICA de esta pantalla se copia TAL CUAL de DoctorScheduleTable.js: los
   manejadores de clic y de menú contextual, el envío por WhatsApp, el borrado
   con su permiso y las funciones de horario son exactamente los mismos. Lo
   único que cambia es lo que se pinta: `AgendaCuadricula` en vez de la tabla
   de filas de 90px.

   Se hace así a propósito. Reescribir esos manejadores mientras se rediseña la
   pantalla mezclaría dos riesgos distintos: si algo se comporta raro después
   de este cambio, la causa está en el render, no en las reglas.

   Único cambio de comportamiento, y es el que se pidió: el menú contextual
   ahora sale en CUALQUIER celda. Antes hacía `return` si no había cita, así
   que sobre un cupo libre no pasaba nada. Ahora un cupo libre ofrece
   "Agendar aquí" -- la misma acción que el clic normal, no una nueva.
   ========================================================================== */

import moment from 'moment/moment';
import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import ContextMenu from '../Configuracion/DoctorSchaduleTable/ContextMenu';
import { EliminarCita } from '../../Service/Service.citas';
import usePermissions from '../../hook/usePermissions';
import AgendaCuadricula from './AgendaCuadricula';

function AgendaContenedor({ doctoresEspecialidades, fecha, show, setShow, setDoctor, setHora,
  setCitaPaciente, citasDoctotes, handleChange,
  cargando = false, error = null, haySeleccion = true, estadosVisibles = null,
  filtroCobertura = null,
  onReintentar, onIrDiaSiguiente }) {
  const [contextMenu, setContextMenu] = useState({ visible: false, position: { x: 0, y: 0 }, options: [] });
  const [holdTimer, setHoldTimer] = useState(null);
  // RBAC-CUTOVER-02: "Eliminar Cita" depende de citas.delete real.
  const { hasPermission } = usePermissions();

  const dias = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];
  const diaActual = dias[moment(fecha).toDate().getDay()];

  
  function sumarMinutos(hora, minutos) {
    const [h, m] = hora.split(':').map(Number);
    const totalMinutos = h * 60 + m + minutos;
    const horasFinal = Math.floor(totalMinutos / 60);
    const minutosFinal = totalMinutos % 60;
    return `${String(horasFinal).padStart(2, '0')}:${String(minutosFinal).padStart(2, '0')}`;
  }

  // `horariosClinica` vivía aquí y calculaba las franjas para la tabla vieja.
  // Ahora las calcula `AgendaCuadricula` con el MISMO `calcularIntervalos` y los
  // mismos tres argumentos, así que aquí quedaba huérfano. Es el único trozo de
  // la copia que se retira, y sólo porque su consumidor se fue con el JSX.

  function isDiaLaboral(doctor) {
    return doctor.doctors_dias_laborables?.[diaActual] === true;
  }

  function isDentroHorario(doctor, hora) {
    const [hi, mi] = doctor.horario_inicio.split(':').map(Number);
    const [hf, mf] = doctor.horario_fin.split(':').map(Number);
    const [hh, mh] = hora.split(':').map(Number);
    const inicioTotal = hi * 60 + mi;
    const finTotal = hf * 60 + mf;
    const horaTotal = hh * 60 + mh;
    return horaTotal >= inicioTotal && horaTotal < finTotal;
  }

  function buscarCita(doctor, hora) {
    return citasDoctotes.find(cita => {
      const citaHora = String(cita.hora_inicio).padStart(5, '0');
      const citaFecha = moment(cita.fecha_inicio).format('YYYY-MM-DD');
      return cita.iddoctor === doctor.iddoctor && citaHora === hora && citaFecha === moment(fecha).format('YYYY-MM-DD');
    });
  }

  const handleCellClick = (doctor, hora, intervalo) => {
    if (!isDiaLaboral(doctor)) {
      return Swal.fire({ icon: 'warning', title: 'No trabaja este día', text: hora });
    }
    if (!isDentroHorario(doctor, hora)) {
      return Swal.fire({ icon: 'warning', title: 'Fuera de horario laboral', text: hora });
    }

    const cita = buscarCita(doctor, hora);
    setHora({ hora_inicio: hora, hora_fin: sumarMinutos(hora, intervalo) });
    if (cita) {
      handleChange({ label: cita.pacientes.nombres_completos, value: cita.pacientes.idpaciente });
      setCitaPaciente({ 
        ...cita.pacientes, 
        ...cita, 
        nombre: cita.pacientes.nombres_completos, 
        id_estado_cita: cita.estado_cita?.idestado_cita || 0 
      });
    } else {
      setCitaPaciente({ 
        idclinica: doctor.idclinica, 
        fecha_inicio: fecha, 
        iddoctor: doctor.iddoctor, 
        hora_inicio: hora, 
        hora_fin: sumarMinutos(hora, intervalo) 
      });
    }
    
    setDoctor(doctor);
    setShow(true);
  };

  function isDoctorPaciente(doctor, hora) {
    const citasDoctorHora = citasDoctotes.filter((cita) => {
      const horaCita = String(cita.hora_inicio).padStart(5, '0');
      const fechaCita = moment(cita.fecha_inicio).format('YYYY-MM-DD');
      return (
        cita.iddoctor === doctor.iddoctor &&
        horaCita === hora &&
        fechaCita === moment(fecha).format('YYYY-MM-DD')
      );
    });
    
    if(citasDoctorHora.length > 0){
      return {
        motivo: citasDoctorHora[0].motivo,

        hora_inicio: citasDoctorHora[0].hora_inicio,
        fecha_inicio: citasDoctorHora[0].fecha_inicio,
        hora_fin: citasDoctorHora[0].hora_fin,
        idpaciente: citasDoctorHora[0].pacientes.idpaciente,
        nombre: citasDoctorHora[0].pacientes.nombres_completos,

        id_estado_cita: citasDoctorHora[0].estado_cita.idestado_cita,
        estado: citasDoctorHora[0].estado_cita.estado,
        fecha: citasDoctorHora[0].fecha_inicio,
        iddoctor: citasDoctorHora[0].iddoctor,
        idcita: citasDoctorHora[0].idcita,
        idtipo_cita: citasDoctorHora[0].idtipo_cita,
        celular: citasDoctorHora[0].pacientes.celular,
      }
    }else{
      return null;
    }
  }
  const handleContextMenu = (e, doctor, hora) => {
    e.preventDefault();
    const cita = buscarCita(doctor, hora);
    if (!cita) return;
    let paciente = isDoctorPaciente(doctor, hora);
    let doctorsito = doctor;
    if(!paciente) return;
  
    // Si ya está abierto y vuelves a hacer click en la misma celda => cerrar
    if (contextMenu.visible && contextMenu.position.targetId === `${doctor.iddoctor}_${hora}`) {
      closeContextMenu();
      return;
    }
  
    const rect = e.currentTarget.getBoundingClientRect();
    let posX = rect.left + rect.width / 2 + window.scrollX;
    let posY = rect.top + rect.height / 2 + window.scrollY;
    const menuWidth = 200, menuHeight = 160;
    const windowWidth = window.innerWidth, windowHeight = window.innerHeight;
  
    if (posX + menuWidth > windowWidth) posX = windowWidth - menuWidth - 10;
    if (posY + menuHeight > windowHeight) posY = windowHeight - menuHeight - 10;
  
    setContextMenu({
      visible: true,
      position: { x: posX, y: posY, targetId: `${doctor.iddoctor}_${hora}` },
      options: [
        {
          label: 'Abrir Consulta',
          action: () => window.open(`/paciente_historial?idpaciente=${cita.pacientes.idpaciente}&idcita=${cita.idcita}`)
        },
        {
          label: 'Enviar WhatsApp',
          action: () => {
           
          const clinica = {
              nombre: "Centro De Especialidades Colon",
              direccion: "Avenida Rodrigo Chávez González Solar 3 manzana 1213 de la Urbanización bosques del salado (Urdenor II) Frente al Parque Empresarial Colon",
              gmap: "https://maps.app.goo.gl/iUwzJJYdqDdNH6bP6",
              nombreatendido: "Admisiones"
          };

            // 🔹 **Llamamos a la función**
            enviarWhatsApp(paciente, doctor, clinica);
            //var win = window.open(wpFullLink);
            //window.open(`https://wa.me/${doctor.celular}`)
            //window.open(`https://wa.me/593987682180`)
            
          },
        },
        ...(hasPermission('citas.delete') ? [{
          label: 'Eliminar Cita',
          action: async () => {
            const confirm = await Swal.fire({
              title: '¿Está seguro?',
              text: `Esta acción eliminará la cita ${cita.idcita}`,
              icon: 'warning',
              showCancelButton: true,
              confirmButtonText: 'Sí, eliminar',
              cancelButtonText: 'Cancelar'
            });

            if (confirm.isConfirmed) {
              const res = await EliminarCita(cita.idcita);
              if (res.success) {
                Swal.fire('¡Eliminado!', '', 'success').then(() => window.location.reload());
              } else {
                Swal.fire('Error', res.message, 'error');
              }
            }
          }
        }] : [])
      ]
    });
  };
  
  useEffect(() => {
    const handleOutsideClick = (e) => {
      const menu = document.getElementById('custom-context-menu');
      if (contextMenu.visible && (!menu || !menu.contains(e.target))) {
        closeContextMenu();
      }
    };
    document.addEventListener('click', handleOutsideClick, true);
    document.addEventListener('contextmenu', handleOutsideClick, true);
    return () => {
      document.removeEventListener('click', handleOutsideClick, true);
      document.removeEventListener('contextmenu', handleOutsideClick, true);
    };
  }, [contextMenu]);

  const closeContextMenu = () => setContextMenu({ ...contextMenu, visible: false });

  function enviarWhatsApp(paciente, doctor, clinica) {
    // Construcción del mensaje con variables dinámicas
    //paciente.celular= 987841862;
    var especialidad = (doctor.especialidades?.especialidad || 'No definida');
    var text = `🏥 *${clinica?.nombre || 'Clínica'}* le informa: %0A
    ━━━━━━━━━━━━━━━━━━━%0A
    👤 *Paciente:* ${paciente?.nombre || 'Desconocido'}%0A
    📆 *Fecha:* ${paciente?.fecha_inicio || '---'}%0A
    ⏰ *Hora:* ${paciente?.hora_inicio || '---'}%0A
    
    👨‍⚕️ *Doctor:* ${doctor?.nombre || 'Doctor'}%0A
    🩺 *Especialidad:* ${especialidad}%0A
    
    📌 *Dirección:* ${clinica?.direccion || ''}%0A
    🗺️ *Ubicación:* ${clinica?.gmap || ''}%0A
    
    ⚠️ *Por favor, llegue 15 minutos antes de su cita y lleve su cédula.*%0A
    ━━━━━━━━━━━━━━━━━━━%0A
    🙋‍♂️ *Atendido por:* ${clinica?.nombreatendido || ''}.`;
    

    // Construcción de la URL de WhatsApp
    // Codificación del texto para evitar problemas con caracteres especiales
    const encodedText = encodeURIComponent(text);
    //var url = `https://wa.me/${paciente.idpaciente}?text=${encodeURIComponent(text)}`;
    var url = `whatsapp://send?phone=593${paciente.celular}&text=${text}`;
    //var url = `https://wa.me/593${paciente.celular}?text=${encodedText}`;
    //alert(url);
    // Abrir WhatsApp con el mensaje prellenado
    window.open(url);
  }

  // El menú contextual sale SIEMPRE (§7 del encargo). Sobre una cita ofrece lo
  // de siempre; sobre un cupo libre, agendar.
  const abrirMenu = (e, doctor, hora) => {
    e.preventDefault();
    const cita = buscarCita(doctor, hora);
    if (cita) return handleContextMenu(e, doctor, hora);

    if (contextMenu.visible && contextMenu.position.targetId === `${doctor.iddoctor}_${hora}`) {
      closeContextMenu();
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    let posX = rect.left + rect.width / 2 + window.scrollX;
    let posY = rect.top + rect.height / 2 + window.scrollY;
    const menuWidth = 200, menuHeight = 160;
    if (posX + menuWidth > window.innerWidth) posX = window.innerWidth - menuWidth - 10;
    if (posY + menuHeight > window.innerHeight) posY = window.innerHeight - menuHeight - 10;

    const intervalo =
      doctoresEspecialidades[0]?.especialidades?.clinica_especialidades?.[0]?.intervalo_tiempo;

    setContextMenu({
      visible: true,
      position: { x: posX, y: posY, targetId: `${doctor.iddoctor}_${hora}` },
      options: [
        {
          label: 'Agendar aquí',
          action: () => {
            closeContextMenu();
            handleCellClick(doctor, hora, intervalo);
          },
        },
      ],
    });
  };

  return (
    <>
      <AgendaCuadricula
        doctores={doctoresEspecialidades}
        citas={citasDoctotes}
        fecha={fecha}
        cargando={cargando}
        error={error}
        haySeleccion={haySeleccion}
        estadosVisibles={estadosVisibles}
        filtroCobertura={filtroCobertura}
        onCupo={handleCellClick}
        onMenuContextual={abrirMenu}
        onReintentar={onReintentar}
        onIrDiaSiguiente={onIrDiaSiguiente}
      />

      {contextMenu.visible && (
        <ContextMenu
          id="custom-context-menu"
          visible={contextMenu.visible}
          position={contextMenu.position}
          options={contextMenu.options}
          onClose={closeContextMenu}
        />
      )}
    </>
  );
}

export default AgendaContenedor;
